import { Card, Col } from "react-bootstrap";
import { User } from "../../types/User";
import {
  Button as AntButton,
  DatePicker,
  Divider,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { Sesso } from "../../enums/Sesso";
import NumericInput from "../../common/NumericInput";
import { RoleLabels, Roles } from "../../enums/Roles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCheck,
  faClock,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { formatExpiry, getSubscriptionStatus } from "../../utils/subscription";

type Props = {
  user?: User;
  isNewUser?: boolean;
  disabled?: boolean;
  onPesoChange: (value: string) => void;
  onAltezzaChange: (value: string) => void;
  /** Existing users only — immediate subscription/access actions. */
  onActivate?: () => void;
  onDeactivate?: () => void;
  onReissue?: () => void;
  onSaveExpiry?: () => void;
};

const EXPIRY_PRESETS: { label: string; months: number }[] = [
  { label: "+1 mese", months: 1 },
  { label: "+3 mesi", months: 3 },
  { label: "+6 mesi", months: 6 },
  { label: "+12 mesi", months: 12 },
];

export default function UserForm({
  user,
  isNewUser,
  disabled,
  onPesoChange,
  onAltezzaChange,
  onActivate,
  onDeactivate,
  onReissue,
  onSaveExpiry,
}: Props) {
  const form = Form.useFormInstance();
  const status = user ? getSubscriptionStatus(user) : null;

  // Presets work off the date already in the field when it is in the future,
  // otherwise off today — so "+1 mese" always lands on a sensible deadline.
  const applyPreset = (months: number) => {
    const current = form.getFieldValue("expiring");
    const base =
      current && dayjs(current).isAfter(dayjs()) ? dayjs(current) : dayjs();
    form.setFieldValue("expiring", base.add(months, "month"));
  };

  const clearExpiry = () => form.setFieldValue("expiring", null);

  return (
    <>
      <Col xs={12} md={6}>
        <Card>
          <Card.Header>
            <Card.Title>Dati Anagrafici</Card.Title>
          </Card.Header>
          <Card.Body>
            <Form.Item
              labelAlign="left"
              label="Nome"
              name="nome"
              rules={[
                {
                  required: true,
                  message: "Il nome è obbligatorio",
                },
              ]}
            >
              <Input
                className="w-50"
                disabled={disabled}
                placeholder="Nome..."
              />
            </Form.Item>
            <Form.Item
              labelAlign="left"
              label="Cognome"
              name="cognome"
              rules={[
                {
                  required: true,
                  message: "Il cognome è obbligatorio",
                },
              ]}
            >
              <Input
                className="w-50"
                disabled={disabled}
                placeholder="Cognome..."
              />
            </Form.Item>
            <Form.Item
              name="birthDate"
              labelAlign="left"
              label="Data di nascita"
              rules={[
                {
                  required: true,
                  message: "La data di nascita è obbligatoria",
                },
              ]}
            >
              <DatePicker
                format="DD/MM/YYYY"
                placeholder="Data di nascita..."
              />
            </Form.Item>
            <Form.Item
              labelAlign="left"
              name="sesso"
              label="Sesso"
              rules={[
                {
                  required: true,
                  message: "Il sesso è obbligatorio",
                },
              ]}
            >
              <Select
                className="w-50"
                placeholder="Sesso..."
                options={[
                  {
                    label: "Uomo",
                    value: Sesso.Male,
                  },
                  {
                    label: "Donna",
                    value: Sesso.Female,
                  },
                ]}
              />
            </Form.Item>
            <Form.Item labelAlign="left" name="peso" label="Peso">
              <Tooltip
                trigger={["focus"]}
                title="Inserisci un valore numerico"
                placement="topLeft"
                classNames={{ root: "numeric-input" }}
              >
                <NumericInput
                  style={{ width: 120 }}
                  value={user?.peso != null ? String(user.peso) : ""}
                  onChange={onPesoChange}
                  suffix="Kg"
                  placeholder="Peso..."
                />
              </Tooltip>
            </Form.Item>
            <Form.Item labelAlign="left" name="altezza" label="Altezza">
              <Tooltip
                trigger={["focus"]}
                title="Inserisci un valore numerico"
                placement="topLeft"
                classNames={{ root: "numeric-input" }}
              >
                <NumericInput
                  style={{ width: 120 }}
                  value={user?.altezza != null ? String(user.altezza) : ""}
                  onChange={onAltezzaChange}
                  suffix="m"
                  placeholder="Altezza..."
                />
              </Tooltip>
            </Form.Item>
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card>
          <Card.Header>
            <Card.Title>Dati di contatto</Card.Title>
          </Card.Header>
          <Card.Body>
            <Form.Item
              labelAlign="left"
              name="email"
              label="E-Mail"
              rules={[
                {
                  required: true,
                  message: "L'indirizzo mail è obbligatorio",
                },
                {
                  type: "email",
                  message: "Inserisci un indirizzo mail valido",
                },
              ]}
            >
              <Input type="email" placeholder="E-Mail..." />
            </Form.Item>
            <Form.Item
              labelAlign="left"
              name="phoneNumber"
              label="Numero di telefono"
              rules={[
                {
                  pattern: /^[0-9]{9,15}$/,
                  message: "Inserisci un numero di telefono valido",
                },
              ]}
            >
              <Input type="text" placeholder="Numero di telefono..." />
            </Form.Item>
            <Form.Item
              labelAlign="left"
              name="username"
              label="Username"
              rules={[
                {
                  required: true,
                  message: "L'username è obbligatorio",
                },
              ]}
            >
              <Input type="text" placeholder="Username..." />
            </Form.Item>
            <Form.Item
              labelAlign="left"
              name="role"
              label="Ruolo"
              rules={[
                {
                  required: true,
                  message: "Il ruolo dell'utente è obbligatorio",
                },
              ]}
            >
              <Select
                className="w-50"
                placeholder="Ruolo..."
                options={[
                  {
                    label: RoleLabels[Roles.Athlete],
                    value: Roles.Athlete,
                  },
                  {
                    label: RoleLabels[Roles.Trainer],
                    value: Roles.Trainer,
                  },
                  {
                    label: RoleLabels[Roles.Superadmin],
                    value: Roles.Superadmin,
                  },
                ]}
              />
            </Form.Item>
          </Card.Body>
        </Card>
      </Col>

      {/* Abbonamento e Accesso — subscription deadline + access actions.
          Profile data is saved with the main form button; subscription
          changes are applied independently (immediate, with their own
          notification emails) so a simple name edit never re-triggers a
          "rinnovo" or silently reactivates a disabled account. */}
      <Col xs={12}>
        <Card className="mt-3">
          <Card.Header className="d-flex align-items-center justify-content-between">
            <Card.Title className="mb-0">Abbonamento e Accesso</Card.Title>
            {!isNewUser && status && (
              <Tag color={status.color} className="fw-bold m-0">
                {status.label}
              </Tag>
            )}
          </Card.Header>
          <Card.Body>
            {!isNewUser && (
              <Typography.Paragraph type="secondary" className="mb-3">
                Scadenza attuale: <strong>{formatExpiry(user?.expiring)}</strong>
              </Typography.Paragraph>
            )}

            <Form.Item
              labelAlign="left"
              name="expiring"
              label="Scadenza abbonamento"
              extra={
                isNewUser
                  ? "Verrà applicata alla creazione dell'utente."
                  : "Imposta una scadenza futura per (ri)attivare l'abbonamento, poi premi “Salva scadenza”."
              }
            >
              <DatePicker
                format="DD/MM/YYYY"
                placeholder="Scadenza..."
                disabled={disabled}
              />
            </Form.Item>

            <Space wrap size={8} className="mb-2">
              {EXPIRY_PRESETS.map((p) => (
                <AntButton
                  key={p.months}
                  size="small"
                  disabled={disabled}
                  onClick={() => applyPreset(p.months)}
                >
                  {p.label}
                </AntButton>
              ))}
              <AntButton
                size="small"
                danger
                type="text"
                disabled={disabled}
                onClick={clearExpiry}
              >
                Rimuovi scadenza
              </AntButton>
            </Space>

            {!isNewUser && (
              <>
                <div>
                  <AntButton
                    type="primary"
                    icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                    disabled={disabled}
                    onClick={onSaveExpiry}
                  >
                    Salva scadenza
                  </AntButton>
                </div>

                <Divider className="my-3" />

                <Space wrap size={8}>
                  {user?.isActive ? (
                    <AntButton
                      danger
                      icon={<FontAwesomeIcon icon={faBan} />}
                      disabled={disabled}
                      onClick={onDeactivate}
                    >
                      Disattiva abbonamento
                    </AntButton>
                  ) : (
                    <AntButton
                      type="primary"
                      icon={<FontAwesomeIcon icon={faCheck} />}
                      disabled={disabled}
                      onClick={onActivate}
                      style={{ background: "#52c41a", borderColor: "#52c41a" }}
                    >
                      Attiva abbonamento
                    </AntButton>
                  )}
                  <AntButton
                    icon={<FontAwesomeIcon icon={faClock} />}
                    disabled={disabled}
                    onClick={onReissue}
                  >
                    Reinvia password temporanea
                  </AntButton>
                </Space>
              </>
            )}
          </Card.Body>
        </Card>
      </Col>
    </>
  );
}
